import { format, formatDistanceToNowStrict } from 'date-fns';
import { ar as arLocale, enUS } from 'date-fns/locale';
import type { LocaleCode } from '@/shared/lib/i18n/locale-context';

/**
 * Date formatting for display.
 *
 * ⚠️ Every call passes an explicit locale. The backend learned this the hard way: on a
 * machine with an Arabic (Saudi) culture, an unqualified date format produces a
 * **Hijri** date — 17 August 2026 became 1448-03-04 — and it did so silently
 * (PROJECT.md §15). date-fns does not read the ambient culture, which is exactly why it
 * is the choice here: the calendar is a decision the call site makes.
 *
 * §2 lists "date-fns + Hijri support" for this panel, so a Hijri display option is
 * wanted. It will be an explicit formatter alongside these, never a default that
 * depends on where the browser happens to be.
 */
const DATE_FNS_LOCALES = { ar: arLocale, en: enUS } as const;

export function formatDateTime(iso: string, locale: LocaleCode): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return format(date, 'yyyy-MM-dd HH:mm', { locale: DATE_FNS_LOCALES[locale] });
}

/**
 * "3 hours ago".
 *
 * ⚠️ `roundingMethod: 'floor'` is not a detail. `formatDistanceToNowStrict` drops the
 * "about", but it still rounds to the NEAREST unit by default — 1 hour 50 minutes came out
 * as "2 hours ago", which is a duration that has not happened yet.
 *
 * Elapsed time floors, the way an age does: an operator reading how long someone has been
 * missing is reading a number they act on, and it must never be larger than the truth.
 * Caught by a test asserting the exact string, after this function shipped with a comment
 * claiming a behaviour it did not have.
 */
export function formatElapsed(iso: string, locale: LocaleCode): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return formatDistanceToNowStrict(date, {
    addSuffix: true,
    roundingMethod: 'floor',
    locale: DATE_FNS_LOCALES[locale],
  });
}

/** The unit suffixes, supplied by the caller from the active dictionary. */
export interface DurationUnits {
  day: string;
  hour: string;
  minute: string;
}

/**
 * Formats a .NET `TimeSpan` as the API sends it — `"1.06:12:30"` or `"06:12:30"`.
 *
 * ⚠️ Parsed, not handed to a date library.
 *
 * The value is a duration, not an instant, and every parser available here expects an instant:
 * `new Date("06:12:30")` is an invalid date and `new Date("1.06:12:30")` is something worse. The API
 * sends this shape because that is how `System.TimeSpan` serialises, and it feeds the board's
 * most-read column.
 *
 * The unit labels are passed in rather than chosen here: §5.8 keeps every user-facing string in the two
 * dictionaries, and a `locale === 'ar' ? 'س' : 'h'` in a shared formatter is exactly the inline literal
 * that rule exists to prevent. (Caught by `conventions.test.ts`, on the first draft of this function.)
 */
export function formatDuration(timeSpan: string, units: DurationUnits): string {
  const match = /^(?:(\d+)\.)?(\d{1,2}):(\d{2}):(\d{2})/.exec(timeSpan);

  if (!match) {
    return '—';
  }

  const days = Number(match[1] ?? 0);
  const hours = Number(match[2]);
  const minutes = Number(match[3]);

  // Floored to the two largest meaningful units, for the reason formatElapsed floors: an operator
  // reading how long someone has been missing must never see a number larger than the truth.
  if (days > 0) {
    return `${days}${units.day} ${hours}${units.hour}`;
  }

  if (hours > 0) {
    return `${hours}${units.hour} ${minutes}${units.minute}`;
  }

  return `${minutes}${units.minute}`;
}
